import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State =
  | { kind: "loading" }
  | { kind: "valid"; email: string }
  | { kind: "already" }
  | { kind: "invalid"; message: string }
  | { kind: "submitting" }
  | { kind: "success" };

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "invalid", message: "Brak tokenu w linku." });
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: ANON_KEY } },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setState({ kind: "invalid", message: data?.error ?? "Nieprawidłowy link." });
          return;
        }
        if (data.already_unsubscribed) {
          setState({ kind: "already" });
          return;
        }
        setState({ kind: "valid", email: data.email ?? "" });
      } catch {
        setState({ kind: "invalid", message: "Nie udało się zweryfikować linku." });
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState({ kind: "submitting" });
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/handle-email-unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setState({ kind: "invalid", message: data?.error ?? "Nie udało się wypisać." });
        return;
      }
      setState({ kind: "success" });
    } catch {
      setState({ kind: "invalid", message: "Nie udało się wypisać." });
    }
  };

  return (
    <>
      <Helmet>
        <title>Wypisz się z powiadomień | GdzieSchronisko.pl</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Wypisanie z powiadomień</CardTitle>
            <CardDescription>GdzieSchronisko.pl</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            {state.kind === "loading" && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {state.kind === "valid" && (
              <>
                <p className="text-sm text-muted-foreground">
                  Czy na pewno chcesz przestać otrzymywać powiadomienia
                  {state.email ? ` na adres ${state.email}` : ""}?
                </p>
                <Button onClick={confirm} className="w-full">
                  Tak, wypisz mnie
                </Button>
              </>
            )}
            {state.kind === "submitting" && (
              <Button disabled className="w-full">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Przetwarzam...
              </Button>
            )}
            {state.kind === "success" && (
              <>
                <CheckCircle2 className="h-10 w-10 text-primary mx-auto" />
                <p className="text-sm">Gotowe - nie będziemy do Ciebie pisać.</p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">Wróć na stronę główną</Link>
                </Button>
              </>
            )}
            {state.kind === "already" && (
              <>
                <CheckCircle2 className="h-10 w-10 text-primary mx-auto" />
                <p className="text-sm">Ten adres jest już wypisany z powiadomień.</p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">Wróć na stronę główną</Link>
                </Button>
              </>
            )}
            {state.kind === "invalid" && (
              <>
                <XCircle className="h-10 w-10 text-destructive mx-auto" />
                <p className="text-sm text-muted-foreground">{state.message}</p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">Wróć na stronę główną</Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </>
  );
};

export default Unsubscribe;
